export interface ThemeConfig {
	name: string;
	displayName: string;
	description: string;
	cssFile: string;
	preview?: {
		primary: string;
		secondary: string;
		accent: string;
	};
}

export const availableThemes: ThemeConfig[] = [
	{
		name: "default",
		displayName: "Default",
		description: "Clean and modern theme with balanced colors",
		cssFile: "",
	},
	{
		name: "catppuccin",
		displayName: "Catppuccin",
		description: "Soothing pastel theme with warm and cozy colors",
		cssFile: "/styles/themes/catppuccin.css",
	},
	{
		name: "candyland",
		displayName: "Candyland",
		description: "A bright, playful candy-inspired theme",
		cssFile: "/styles/themes/candyland.css",
	},
	{
		name: "cosmic-night",
		displayName: "Cosmic Night",
		description: "Deep space theme with dark blues and purples",
		cssFile: "/styles/themes/cosmic-night.css",
	},
	{
		name: "supabase",
		displayName: "Supabase",
		description: "Modern theme inspired by Supabase branding",
		cssFile: "/styles/themes/supabase.css",
	},
];

export const defaultTheme = "default";

export function getThemeByName(name: string): ThemeConfig | undefined {
	return availableThemes.find((theme) => theme.name === name);
}

export function getThemeCssFile(themeName: string): string {
	const theme = getThemeByName(themeName);
	return theme?.cssFile || availableThemes[0].cssFile;
}

// Function to extract colors from CSS file
export async function getThemeColors(themeName: string): Promise<{ primary: string; secondary: string; accent: string } | null> {
	const theme = getThemeByName(themeName);
	if (!theme) return null;

	try {
		// Fetch the CSS file
		const response = await fetch(theme.cssFile);
		if (!response.ok) return null;

		const cssText = await response.text();

		// Extract color values using regex
		const primaryMatch = cssText.match(/--primary:\s*([^;]+);/);
		const secondaryMatch = cssText.match(/--secondary:\s*([^;]+);/);
		const accentMatch = cssText.match(/--accent:\s*([^;]+);/);

		if (primaryMatch?.[1] && secondaryMatch?.[1] && accentMatch?.[1]) {
			return {
				primary: primaryMatch[1].trim(),
				secondary: secondaryMatch[1].trim(),
				accent: accentMatch[1].trim(),
			};
		}
	} catch (error) {
		console.error("Failed to load theme colors:", error);
	}

	return null;
}

// Function to get theme with colors (for use in components)
export async function getThemeWithColors(themeName: string): Promise<(ThemeConfig & { preview: { primary: string; secondary: string; accent: string } }) | null> {
	const theme = getThemeByName(themeName);
	if (!theme) return null;

	const colors = await getThemeColors(themeName);
	if (!colors) return null;

	return {
		...theme,
		preview: colors,
	};
}

// Function to get all themes with colors
export async function getAllThemesWithColors(): Promise<(ThemeConfig & { preview: { primary: string; secondary: string; accent: string } })[]> {
	const themesWithColors = await Promise.all(
		availableThemes.map(async (theme) => {
			const colors = await getThemeColors(theme.name);
			return {
				...theme,
				preview: colors || { primary: "#000", secondary: "#666", accent: "#333" }, // fallback colors
			};
		})
	);

	return themesWithColors;
}
